import { z } from 'zod';
import { DecimalStringSchema } from './primitives.js';

// Wire contracts of worker mutation endpoints (apps/worker/src/routes/*), shared
// so the web client validates exactly what the worker emits.

export const OkResponseSchema = z.object({ ok: z.boolean() });
export type OkResponse = z.infer<typeof OkResponseSchema>;

export const ClassifyRangeSummarySchema = z.object({
  chainId: z.number().int().positive(),
  txCount: z.number().int().nonnegative(),
  classifiedCount: z.number().int().nonnegative(),
  unknownCount: z.number().int().nonnegative(),
  overrideAppliedCount: z.number().int().nonnegative(),
});

export const ReclassifyAllResponseSchema = z.object({
  summaries: z.array(ClassifyRangeSummarySchema),
  pricesBackfilling: z.boolean(),
});
export type ReclassifyAllResponse = z.infer<typeof ReclassifyAllResponseSchema>;

export const ResyncChainResultSchema = z.object({
  chainId: z.number().int().positive(),
  ok: z.boolean(),
  error: z.string().optional(),
  rawTxCount: z.number().int().nonnegative(),
  transferCount: z.number().int().nonnegative(),
  internalTxCount: z.number().int().nonnegative(),
  lastBlock: z.number().int().nonnegative(),
  classifiedCount: z.number().int().nonnegative(),
});

export const ResyncWalletResponseSchema = z.object({
  walletId: z.number().int().positive(),
  results: z.array(ResyncChainResultSchema),
  pricesBackfilling: z.boolean(),
});
export type ResyncWalletResponse = z.infer<typeof ResyncWalletResponseSchema>;

export const BackfillEventPricesResponseSchema = z.object({
  totalPairs: z.number().int().nonnegative(),
  alreadyCached: z.number().int().nonnegative(),
  newlyFetched: z.number().int().nonnegative(),
  assetsEnriched: z.number().int().nonnegative(),
  skippedNoCoingecko: z.number().int().nonnegative(),
  remaining: z.number().int().nonnegative(),
  errors: z.array(
    z.object({
      assetId: z.number().int().positive(),
      timestamp: z.number().int().nonnegative(),
      message: z.string(),
    }),
  ),
});
export type BackfillEventPricesResponse = z.infer<typeof BackfillEventPricesResponseSchema>;

export const RefreshCurrentPricesResponseSchema = z.object({
  asOf: z.number().int().nonnegative(),
  heldAssets: z.number().int().nonnegative(),
  refreshed: z.number().int().nonnegative(),
  skippedNoPrice: z.number().int().nonnegative(),
  errors: z.array(z.object({ assetId: z.number().int().positive(), message: z.string() })),
});
export type RefreshCurrentPricesResponse = z.infer<typeof RefreshCurrentPricesResponseSchema>;

export const FreezeClosingPricesResponseSchema = z.object({
  frozen: z.number().int().nonnegative(),
  skipped: z.array(z.number().int().positive()),
});
export type FreezeClosingPricesResponse = z.infer<typeof FreezeClosingPricesResponseSchema>;

export const BulkOverrideResponseSchema = z.object({
  succeeded: z.array(z.number().int().positive()),
  conflicts: z.array(z.object({ rawTxId: z.number().int().positive(), message: z.string() })),
  failed: z.array(z.object({ rawTxId: z.number().int().positive(), message: z.string() })),
});
export type BulkOverrideResponse = z.infer<typeof BulkOverrideResponseSchema>;

export const AuditLogEntrySchema = z.object({
  id: z.number().int().positive(),
  timestamp: z.number().int().nonnegative(),
  action: z.string(),
  entityType: z.string(),
  entityId: z.number().int().nullable(),
  details: z.string().nullable(),
  actorEmail: z.string().nullable().default(null),
});
export type AuditLogEntry = z.infer<typeof AuditLogEntrySchema>;

export const AuditLogResponseSchema = z.object({
  entries: z.array(AuditLogEntrySchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type AuditLogResponse = z.infer<typeof AuditLogResponseSchema>;

// Subset of the worker ClosePeriodResult the UI consumes (extra keys stripped).
export const ClosePeriodResultResponseSchema = z.object({
  fiscalPeriodId: z.number().int().positive(),
  status: z.literal('CLOSED'),
  snapshotCount: z.number().int().nonnegative(),
  cryEntriesCreated: z.number().int().nonnegative(),
  valuationEntriesCreated: z.number().int().nonnegative(),
  latentGain: DecimalStringSchema,
  latentLoss: DecimalStringSchema,
  provision: DecimalStringSchema,
  warnings: z.array(z.string()),
});
export type ClosePeriodResultResponse = z.infer<typeof ClosePeriodResultResponseSchema>;
